import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Calculator, DollarSign, TrendingUp, Users, ShoppingCart, Percent } from 'lucide-react';
import * as SliderPrimitive from '@radix-ui/react-slider';

const ROISlider = ({ value, onValueChange, min, max, step, color, testId }) => (
  <SliderPrimitive.Root
    value={[value]}
    onValueChange={(val) => onValueChange(val[0])}
    max={max}
    min={min}
    step={step}
    className="relative flex w-full touch-none select-none items-center py-2 cursor-pointer"
    data-testid={testId}
  >
    <SliderPrimitive.Track className="relative h-3 w-full grow overflow-hidden rounded-full bg-slate-200">
      <SliderPrimitive.Range className={`absolute h-full rounded-full ${color}`} />
    </SliderPrimitive.Track>
    <SliderPrimitive.Thumb className={`block h-7 w-7 rounded-full ${color} shadow-lg shadow-current/30 border-[3px] border-white ring-2 ring-slate-200 transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 hover:scale-110 active:scale-95`} />
  </SliderPrimitive.Root>
);

const ROICalculator = () => {
  const [visitors, setVisitors] = useState(50000);
  const [conversionRate, setConversionRate] = useState(2);
  const [avgOrderValue, setAOV] = useState(75);

  const results = useMemo(() => {
    if (visitors === 0 || conversionRate === 0 || avgOrderValue === 0) {
      return { currentRevenue: 0, newRevenue: 0, additionalRevenue: 0, percentIncrease: 0, newConversionRate: '0.0' };
    }
    const currentRevenue = visitors * (conversionRate / 100) * avgOrderValue;
    const aiConversionBoost = 0.30;
    const aiCartRecovery = 0.15;
    const newConversionRate = conversionRate * (1 + aiConversionBoost);
    const recoveredCarts = visitors * (conversionRate / 100) * 0.7 * aiCartRecovery;
    const newRevenue = visitors * (newConversionRate / 100) * avgOrderValue + recoveredCarts * avgOrderValue;
    const additionalRevenue = newRevenue - currentRevenue;
    const percentIncrease = currentRevenue > 0 ? ((newRevenue - currentRevenue) / currentRevenue) * 100 : 0;

    return {
      currentRevenue,
      newRevenue,
      additionalRevenue,
      percentIncrease: Math.round(percentIncrease),
      newConversionRate: newConversionRate.toFixed(1)
    };
  }, [visitors, conversionRate, avgOrderValue]);

  const formatCurrency = (num) => {
    if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `$${(num / 1000).toFixed(0)}K`;
    return `$${Math.round(num)}`;
  };

  return (
    <section className="py-16 sm:py-24 md:py-32 bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-900 relative overflow-hidden" data-testid="roi-calculator">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,white_1px,transparent_1px)] bg-[size:32px_32px]" />
      </div>
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-amber-500/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 left-0 w-[350px] h-[350px] bg-indigo-500/10 rounded-full blur-[100px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <motion.div
          className="text-center mb-12 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-400/30 rounded-full px-3 sm:px-4 py-2 mb-4 sm:mb-6">
            <Calculator className="w-4 h-4 text-amber-400" />
            <span className="text-xs sm:text-sm font-bold text-amber-300">ROI Calculator</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 sm:mb-5">
            See Your{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">Revenue Potential</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed px-4">
            Slide the controls to match your store's metrics and see how much Arismate AI can grow your revenue.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
          {/* Sliders */}
          <motion.div
            className="bg-white/5 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-white/10"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg sm:text-xl font-bold text-white mb-6 sm:mb-8">Your Store Metrics</h3>

            {/* Monthly Visitors */}
            <div className="mb-8 sm:mb-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                    <Users className="w-4 h-4 text-violet-400" />
                  </div>
                  <span className="text-sm sm:text-base font-semibold text-slate-300">Monthly Visitors</span>
                </div>
                <span className="text-xl sm:text-2xl font-extrabold text-white font-mono" data-testid="roi-visitors-value">{visitors.toLocaleString()}</span>
              </div>
              <ROISlider value={visitors} onValueChange={setVisitors} min={0} max={500000} step={1000} color="bg-violet-500" testId="roi-visitors-slider" />
              <div className="flex justify-between mt-2 text-xs text-slate-500">
                <span>0</span>
                <span>500K</span>
              </div>
            </div>

            {/* Conversion Rate */}
            <div className="mb-8 sm:mb-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <Percent className="w-4 h-4 text-amber-400" />
                  </div>
                  <span className="text-sm sm:text-base font-semibold text-slate-300">Conversion Rate</span>
                </div>
                <span className="text-xl sm:text-2xl font-extrabold text-white font-mono" data-testid="roi-conversion-value">{conversionRate}%</span>
              </div>
              <ROISlider value={conversionRate} onValueChange={setConversionRate} min={0} max={10} step={0.5} color="bg-amber-500" testId="roi-conversion-slider" />
              <div className="flex justify-between mt-2 text-xs text-slate-500">
                <span>0%</span>
                <span>10%</span>
              </div>
            </div>

            {/* Average Order Value */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <ShoppingCart className="w-4 h-4 text-emerald-400" />
                  </div>
                  <span className="text-sm sm:text-base font-semibold text-slate-300">Avg. Order Value</span>
                </div>
                <span className="text-xl sm:text-2xl font-extrabold text-white font-mono" data-testid="roi-aov-value">${avgOrderValue}</span>
              </div>
              <ROISlider value={avgOrderValue} onValueChange={setAOV} min={0} max={500} step={5} color="bg-emerald-500" testId="roi-aov-slider" />
              <div className="flex justify-between mt-2 text-xs text-slate-500">
                <span>$0</span>
                <span>$500</span>
              </div>
            </div>
          </motion.div>

          {/* Results */}
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            {/* Current Revenue */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-5 sm:p-6 border border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-slate-700 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-slate-400" />
                </div>
                <span className="text-sm sm:text-base font-semibold text-slate-400">Current Monthly Revenue</span>
              </div>
              <p className="text-2xl sm:text-3xl font-extrabold text-white font-mono" data-testid="roi-current-revenue">
                {formatCurrency(results.currentRevenue)}
              </p>
            </div>

            {/* Projected Revenue - Hero */}
            <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl sm:rounded-3xl p-6 sm:p-8 text-white relative overflow-hidden shadow-xl shadow-amber-500/20">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,white_1px,transparent_1px)] bg-[size:24px_24px]" />
              </div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm sm:text-base font-semibold text-amber-100">Projected Revenue with Arismate AI</span>
                </div>
                <p className="text-3xl sm:text-4xl md:text-5xl font-extrabold font-mono mb-2" data-testid="roi-projected-revenue">
                  {formatCurrency(results.newRevenue)}
                </p>
                <p className="text-amber-100 text-sm sm:text-base">
                  New conversion rate: {results.newConversionRate}%
                </p>
              </div>
            </div>

            {/* Additional Revenue */}
            <div className="bg-emerald-500/15 rounded-2xl sm:rounded-3xl p-5 sm:p-6 border border-emerald-400/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm sm:text-base font-semibold text-emerald-400 mb-1">Additional Monthly Revenue</p>
                  <p className="text-2xl sm:text-3xl font-extrabold text-white font-mono" data-testid="roi-additional-revenue">
                    +{formatCurrency(results.additionalRevenue)}
                  </p>
                </div>
                <div className="bg-emerald-500 text-white px-4 py-2 rounded-xl text-center shadow-lg">
                  <p className="text-2xl sm:text-3xl font-extrabold" data-testid="roi-percent-increase">+{results.percentIncrease}%</p>
                  <p className="text-xs text-emerald-100">growth</p>
                </div>
              </div>
            </div>

            <p className="text-xs sm:text-sm text-slate-500 text-center pt-2">
              Based on +30% conversion boost and 15% cart recovery rate from AI automation
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;
